import React from "react";

const RefundPolicy = () => {
  return (
    <div className="max-w-5xl mx-auto py-8 sm:py-12 px-3 sm:px-6">
      {/* Card */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-gradient-to-r from-purple-100 via-purple-50 to-white">
          {/* Left - Title */}
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 sm:w-14 h-12 sm:h-14 rounded-lg bg-purple-300/20 ring-1 ring-purple-300">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-6" />
              </svg>
            </div>

            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
                Refund & Return / Exchange Policy
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Aavryaafabhub — Fair, clear, and customer-first fabric returns.
              </p>
            </div>
          </div>

          {/* Right - Buttons */}
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-3">
            <a
              href="mailto:aavryaafabhub@gmail.com"
              className="px-3 sm:px-4 py-2 rounded-lg bg-purple-300/20 text-purple-700 ring-1 ring-purple-100 hover:bg-purple-300/30 text-sm sm:text-base"
            >
              Email Us
            </a>
            <a
              href="https://wa.me/919944592904"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 sm:px-4 py-2 rounded-lg bg-purple-600 text-white hover:opacity-95 text-sm sm:text-base"
            >
              WhatsApp
            </a>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 sm:p-8">
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            At <strong>AavryaaFabHub</strong>, we carefully inspect every fabric before dispatch.
            If you are not satisfied, please follow the process below — we try to make returns simple
            while protecting fabric integrity and fairness for everyone.
          </p>

          {/* Pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {[
              "⏱ 7 days to request",
              "⚠️ Unwashed & unused only",
              "✂️ Custom cuts non-returnable"
            ].map((pill, i) => (
              <span
                key={i}
                className="inline-block bg-purple-50 text-purple-700 text-xs sm:text-sm font-medium px-3 py-1 rounded-full ring-1 ring-purple-100"
              >
                {pill}
              </span>
            ))}
          </div>

          {/* Sections */}
          {[
            {
              title: "1. Eligibility",
              content: [
                "Request must be raised within 7 days of delivery.",
                "Fabric must be unused, unwashed, unstitched and in the original cut length.",
                "Include original tags, invoice, and packaging when returning.",
                "Custom-printed fabrics and specially-tailored orders are not eligible for return."
              ]
            },
            {
              title: "2. How to Request",
              type: "ol",
              content: [
                "Email aavryaafabhub@gmail.com with your order number, reason, and photos of the item (if damaged).",
                "We will review and reply within 48 hours with next steps.",
                "If approved, we arrange reverse pickup (domestic) or give return instructions for your region."
              ]
            },
            {
              title: "3. Refund & Replacement",
              content: [
                "Approved refunds are processed to the original payment method within 5–15 business days.",
                "If you received the wrong product, we will replace it at no extra cost after verification.",
                "If coupon/discount was applied, refund amount will be adjusted accordingly."
              ]
            },
            {
              title: "4. Shipping & Pickup",
              paragraph: [
                "We provide a free reverse pickup service for most domestic pin codes. If pickup is not available, you may need to courier the item back — we will reimburse reasonable return postage upon approval.",
                "Please keep the parcel ready and include a copy of the invoice or your order number."
              ]
            },
            {
              title: "5. Exceptions",
              content: [
                "Sale/clearance items are final sale unless defective or wrong item shipped.",
                "Fabrics cut below the minimum return length are non-returnable.",
                "Product damaged after delivery may not be accepted."
              ]
            }
          ].map((sec, idx) => (
            <section className="mb-6" key={idx}>
              <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
                {sec.title}
              </h2>
              {sec.paragraph ? (
                sec.paragraph.map((p, i) => (
                  <p key={i} className="text-gray-600 text-sm sm:text-base mb-2">
                    {p}
                  </p>
                ))
              ) : sec.type === "ol" ? (
                <ol className="list-decimal pl-6 text-gray-600 space-y-2 text-sm sm:text-base">
                  {sec.content.map((item, i) => <li key={i}>{item}</li>)}
                </ol>
              ) : (
                <ul className="list-disc pl-6 text-gray-600 space-y-2 text-sm sm:text-base">
                  {sec.content.map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              )}
            </section>
          ))}

          {/* CTA */}
          <div className="mt-4 border-t pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-gray-600 text-xs sm:text-sm">
                Need help now? Contact our support team:
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <a
                  href="tel:+919944592904"
                  className="text-gray-800 font-medium hover:underline text-sm sm:text-base"
                >
                  📞 +91 99445 92904
                </a>
                <a
                  href="mailto:aavryaafabhub@gmail.com"
                  className="text-purple-700 font-medium hover:underline text-sm sm:text-base"
                >
                  ✉️ aavryaafabhub@gmail.com
                </a>
              </div>
            </div>

            <a
              href="https://wa.me/919944592904?text=Hi%20I%20need%20help%20with%20my%20order"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-lg px-4 py-2 bg-purple-600 text-white shadow-sm hover:bg-purple-700 text-sm sm:text-base"
            >
              Chat on WhatsApp
            </a>
          </div>

          <p className="text-xs sm:text-sm text-gray-400 mt-4">
            📅 Last Updated: August 11, 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
